/**
 * Deberes y plazos de la Ley 21.719 para el DPO — contenido de la guía del panel.
 *
 * Referencias de artículo y página al PDF de la ley
 * (smart_back/docs/ai/Ley-21719_13-DIC-2024.pdf). Si se cambia un plazo aquí, hay
 * que cambiarlo también en el cálculo: `privacy.constants.ts` de server_admin.
 *
 * `steps` es el procedimiento que se despliega al abrir la fila. Frases cortas y
 * accionables: qué apretar, qué guardar, qué mirar. No es un manual.
 */

export const PRIVACY_DEADLINES = [
  {
    what: 'Acusar recibo',
    when: 'Al ingresar',
    note: 'Desde que llega el correo',
    cite: 'Art. 11, pág. 10',
    steps: [
      'Botón "Acusar recibo" en la ficha del caso.',
      'Solo confirma que llegó: todavía no hay que resolver nada.',
      'El plazo de 30 días ya viene corriendo desde que entró el correo, no desde el acuse.',
    ],
  },
  {
    what: 'Responder la solicitud',
    when: '30 días corridos',
    note: 'Desde que llega, no desde que se lee',
    cite: 'Art. 11, pág. 10',
    steps: [
      'Corridos: cuentan fines de semana y feriados.',
      'Se despacha con "Conceder y responder", "Conceder en parte" o "Denegar".',
      'Hay que guardar la respuesta enviada y a quién se le envió.',
      'Responder tarde o incompleto ya es infracción leve (Art. 34 bis c).',
    ],
  },
  {
    what: 'Prórroga',
    when: '+30 días corridos',
    note: 'Una sola vez por caso',
    cite: 'Art. 11, pág. 10',
    steps: [
      'Una sola vez: el panel no deja usarla dos veces.',
      'Pide el motivo y queda registrado.',
      'Úsala antes de que venza el plazo original, no después.',
    ],
  },
  {
    what: 'Decidir un bloqueo temporal',
    when: '2 días hábiles',
    note: 'Desde que el titular lo pide',
    cite: 'Art. 11, pág. 11',
    steps: [
      'Se registra la solicitud y después se concede o se rechaza.',
      'Mientras no se decide, no se puede tratar ese dato.',
      'El bloqueo suspende el tratamiento; no borra ni impide almacenar.',
      'Si se rechaza, la decisión hay que comunicarla a la Agencia.',
    ],
  },
  {
    what: 'Informar plazo de reclamo',
    when: '30 días hábiles',
    note: 'Se le informa al denegar',
    cite: 'Art. 11, pág. 11',
    steps: [
      'Va dentro de la respuesta cuando se deniega, aunque sea en parte.',
      'Hay que decirle que puede reclamar ante la Agencia y en cuánto tiempo.',
      'El panel lo marca, pero el texto tiene que ir en el correo al titular.',
    ],
  },
  {
    what: 'Reportar incidente a la Agencia',
    when: 'Sin dilaciones indebidas',
    note: 'Desde la detección',
    cite: 'Art. 14 sexies, pág. 15',
    steps: [
      'Registrar naturaleza, efectos, categorías de datos, número aproximado de titulares y medidas tomadas.',
      'Si hay datos sensibles, de menores de 14 o financieros, además hay que avisar a los titulares.',
      'Si no se puede avisar uno por uno, se publica en un medio de alcance nacional.',
    ],
  },
  {
    what: 'Acceso gratuito garantizado',
    when: '1 vez por trimestre',
    note: 'Por titular',
    cite: 'Art. 10, pág. 9',
    steps: [
      'Acceso y portabilidad: gratis al menos una vez por trimestre.',
      'Rectificación, supresión y oposición: siempre gratis.',
      'Recién desde la segunda vez en el trimestre se pueden cobrar los costos directos.',
    ],
  },
]

/** El sábado no es hábil: es la regla de plazos administrativos de la ley 19.880. */
export const BUSINESS_DAY_RULE =
  'Día hábil = lunes a viernes, sin feriados. El sábado no cuenta.'

export const PRIVACY_RESPONSE_FORM = [
  'Por escrito, al correo o domicilio que indicó el titular.',
  'Guardar el contenido íntegro, la fecha y el destinatario.',
  'Si se deniega, aunque sea en parte: fundar la causal e informar los 30 días hábiles para reclamar ante la Agencia.',
  'Si se rectifica, suprime u opone: avisar a quienes se les comunicó el dato.',
]

/** Art. 7, pág. 7 — inciso segundo. */
export const ERASURE_EXCEPTIONS = [
  'Obligación legal o contrato con el titular',
  'Defensa de una reclamación',
  'Función pública o interés público',
  'Salud pública',
  'Fines históricos, estadísticos o científicos',
  'Libertad de informar',
]

export const PRIVACY_HOUSE_RULES = [
  {
    rule: 'Cada caso dice qué pide el titular y qué se va a hacer',
    detail:
      'Se registra al abrirlo y antes de responder. Es lo que el equipo ejecuta y lo que va en la respuesta.',
    cite: 'Art. 11, pág. 10',
  },
  {
    rule: 'Si los datos son de una empresa cliente, no resolvemos nosotros',
    detail: 'Escalar al responsable y responder al titular igual, dentro del plazo.',
    cite: 'Art. 15 bis, pág. 17',
  },
  {
    rule: 'Denegar es solo del DPO',
    detail: 'Ni un ADMIN puede. Hay que reingresar la contraseña del portal.',
    cite: 'Art. 11, pág. 11',
  },
  {
    rule: 'Las acciones sensibles piden la contraseña del DPO',
    detail: 'Es la misma del portal, comparada contra el hash bcrypt. No se guarda.',
    cite: 'A-30 §6.7',
  },
  {
    rule: 'El borrado sale de un job, nunca de la base a mano',
    detail: 'Sin jobId no hay evidencia de qué se borró.',
    cite: 'Art. 14 quinquies, pág. 15',
  },
  {
    rule: 'El caso no se cierra con bitácora pendiente',
    detail: 'Primero despachar los eventos; la constancia es la prueba.',
    cite: 'Art. 11, pág. 10',
  },
]
